import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/vanilla/shallow';

import { TCreateTProject, TProject, TProjects, TUpdateTProject, IProjectResponse } from 'src/types/Projects';
import ProjectsService from 'src/services/ProjectsService';

interface IState {
    projects: TProjects;

    activeProject: TProject | null;
}

interface IActions {
    setProjects: (projects: TProjects) => void;

    createProject: (project?: TCreateTProject) => Promise<IProjectResponse>;

    updateProject: (project: TUpdateTProject) => Promise<void>;

    deleteProject: (id: number) => Promise<void>;

    setActiveProject: (arg: TProject | null) => void;

    setActiveProjectById: (id: number) => void;
    clear: () => void;
}

const useProjectsStore = create<IState & IActions>(
    (set, get) => ({
        projects: [],

        activeProject: null,
        clear: () => set({ projects: [], activeProject: null }),
        setActiveProject: (project) => {
            set({ activeProject: project });
        },

        setActiveProjectById: (activeProjectId) => {
            const { projects } = get();
            const activeProject = projects.find(({ id }) => id === activeProjectId);
            if (activeProject) {
                set({ activeProject });
            }
        },

        setProjects: (projects) => {
            const { activeProject, setActiveProject, setActiveProjectById } = get();
            set({ projects });

            if (!activeProject?.id && projects.length > 0) {
                setActiveProject(projects[0]);
            } else if (activeProject?.id) {
                setActiveProjectById(activeProject.id);
            }
        },

        createProject: async (
            { name = 'Мой проект', ...project } = { name: 'Мой проект', keywords: [], bundles: [] }
        ) => {
            try {
                const data = await ProjectsService.createProject({ name, ...project });
                const { projects } = get();

                set({
                    projects: [...projects, data],
                    activeProject: data,
                });

                return Promise.resolve(data);
            } catch (err) {
                return Promise.reject(err);
            }
        },

        updateProject: async (project: TUpdateTProject) => {
            try {
                const { projects, setProjects, activeProject, setActiveProject } = get();

                if (!project.id) {
                    throw new Error('ID проекта не указан');
                }

                const data = await ProjectsService.updateProject(project);

                const newProjects = projects.map((project) => {
                    if (project.id === data.id) {
                        return data;
                    }
                    return project;
                });

                setProjects(newProjects);

                // Обновляем activeProject если это тот же проект
                if (activeProject?.id === data.id) {
                    setActiveProject(data);
                }

                return Promise.resolve();
            } catch (err) {
                return Promise.reject(err);
            }
        },

        deleteProject: async (id: number) => {
            try {
                const { projects, activeProject, setActiveProject } = get();

                await ProjectsService.deleteProject(id);

                const newProjects = projects.filter((project) => project.id !== id);

                set({ projects: newProjects });

                // Если удаленный проект был активным, сбрасываем activeProject
                if (activeProject?.id === id) {
                    setActiveProject(newProjects.length > 0 ? newProjects[0] : null);
                }

                return Promise.resolve();
            } catch (err) {
                return Promise.reject(err);
            }
        },
    }),
    shallow
);

export default useProjectsStore;
